/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BetslipRequestData, TBetslipData } from "../BetslipV3/types/types";
import {
  luckyPickInitialState,
  TUpdateHorses,
  TSetTotalStake,
  TUpdateStakeForBetType,
  TUpdateHorse,
  TUpdateHighlightedHorse
} from "./luckyPickTypes";
import { RootState } from "../../store";
import authorizedAxios from "../../apiJwtClient";
import {
  checkIfPayoutIsOverLimit,
  getNewStakeFactorForPlaceTreble,
  getNewStakeFactorForWinDouble,
  getNewStakeFactorForWinPlaceDouble,
  getOdds,
  getPayouts,
  updateTotalStakeAndPayout
} from "./luckyPickUtils";
import { BET_TYPES } from "../../../components/views/luckyPickV3/helpers/luckyPickConstants";

const configConst = (state: RootState) => state.config.data;

/*
 * Thunk to get the betting data from SyX
 */
export const getLuckyPickData: any = createAsyncThunk(
  "getLuckyPickData",
  async (
    { selection, meetingId }: BetslipRequestData,
    { getState }
  ): Promise<TBetslipData> => {
    const config = configConst(getState() as RootState);

    const bettingData = await authorizedAxios.post(
      `${config.puntersApi}/api/betSlip/getSpecialBetslip/${meetingId}`,
      selection
    );

    return bettingData.data;
  }
);

const LuckyPickSlice = createSlice({
  name: "LuckyPickSlice",
  initialState: luckyPickInitialState,
  reducers: {
    /*
     * Sets the Lucky Pick initial state
     * Params: Entire Lucky Pick state
     */
    setLuckyPickState: (state, action) => {
      return { ...state, ...action.payload };
    },
    /*
     * Update the Win Double bet type
     * Params: horses list
     */
    updateWinDouble: (state, action: PayloadAction<TUpdateHorses>) => {
      const { horses } = action.payload;
      const { limits } = state;
      const odds = getOdds(horses);
      const payout = getPayouts(horses, state.winDouble.stake);

      state.winDouble.horses = horses;
      state.winDouble.odds = odds;
      state.winDouble.payout = payout;
      state.winDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
        BET_TYPES.WIN_DOUBLE,
        horses,
        payout,
        limits!
      );
    },
    /*
     * Update the Place Treble bet type
     * Params: horses list
     */
    updatePlaceTreble: (state, action: PayloadAction<TUpdateHorses>) => {
      const { horses } = action.payload;
      const { limits } = state;
      const odds = getOdds(horses);
      const payout = getPayouts(horses, state.placeTreble.stake);

      state.placeTreble.horses = horses;
      state.placeTreble.odds = odds;
      state.placeTreble.payout = payout;
      state.winDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
        BET_TYPES.PLACE_TREBLE,
        horses,
        payout,
        limits!
      );
    },
    /*
     * Update the Win Place Double bet type
     * Params: horses list
     */
    updateWinPlaceDouble: (state, action: PayloadAction<TUpdateHorses>) => {
      const { horses } = action.payload;
      const { limits } = state;
      const odds = getOdds(horses);
      const payout = getPayouts(horses, state.winPlaceDouble.stake);

      state.winPlaceDouble.horses = horses;
      state.winPlaceDouble.odds = odds;
      state.winPlaceDouble.payout = payout;
      state.winDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
        BET_TYPES.WIN_PLACE_DOUBLE,
        horses,
        payout,
        limits!
      );
    },
    /*
     * Calculate the total stake and payout from the totals for each bet type
     * Params: none
     */
    setTotalStakeAndPayout: (state) => {
      const { winDouble, placeTreble, winPlaceDouble } = state;
      const { updatedStake, updatedPayout } = updateTotalStakeAndPayout(
        winDouble,
        placeTreble,
        winPlaceDouble
      );

      state.totalStake = updatedStake;
      state.totalPayout = updatedPayout;
    },
    /*
     * Distributes the total stake in a 1:2:2 ratio between the bet types
     * Under certain conditions, the total stake is distrubted differently
     * Params: Total Stake
     */
    distributeTotalStake: (state, action: PayloadAction<TSetTotalStake>) => {
      const { stake } = action.payload;
      const { limits } = state;

      state.totalStake = stake;

      // Win Double
      const winDoubleStake = stake * getNewStakeFactorForWinDouble(state);
      const winDoubleHorses = state.winDouble.horses;
      const winDoublePayout = getPayouts(winDoubleHorses, winDoubleStake);
      state.winDouble.stake = winDoubleStake;
      state.winDouble.payout = winDoublePayout;
      state.winDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
        BET_TYPES.WIN_DOUBLE,
        winDoubleHorses,
        winDoublePayout,
        limits!
      );

      // Place Treble
      const placeTrebleStake = stake * getNewStakeFactorForPlaceTreble(state);
      const placeTrebleHorses = state.placeTreble.horses;
      const placeTreblePayout = getPayouts(placeTrebleHorses, placeTrebleStake);
      state.placeTreble.stake = placeTrebleStake;
      state.placeTreble.payout = placeTreblePayout;
      state.placeTreble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
        BET_TYPES.PLACE_TREBLE,
        placeTrebleHorses,
        placeTreblePayout,
        limits!
      );

      // Win/Place Double
      const winPlaceDoubleStake =
        stake * getNewStakeFactorForWinPlaceDouble(state);
      const winPlaceDoubleHorses = state.winPlaceDouble.horses;
      const winPlaceDoublePayout = getPayouts(
        winPlaceDoubleHorses,
        winPlaceDoubleStake
      );
      state.winPlaceDouble.stake = winPlaceDoubleStake;
      state.winPlaceDouble.payout = winPlaceDoublePayout;
      state.winPlaceDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
        BET_TYPES.WIN_PLACE_DOUBLE,
        winPlaceDoubleHorses,
        winDoublePayout,
        limits!
      );

      state.totalPayout =
        winDoublePayout + placeTreblePayout + winPlaceDoublePayout;
    },
    /*
     * Update the stake for a specific bet type
     * Params: bet type & stake
     */
    setUpdateStakePerBetType: (
      state,
      action: PayloadAction<TUpdateStakeForBetType>
    ) => {
      const { betType, stake } = action.payload;
      const { limits } = state;

      if (betType === BET_TYPES.WIN_DOUBLE) {
        const { horses } = state.winDouble;
        const payout = getPayouts(horses, stake);

        state.winDouble.stake = stake;
        state.winDouble.payout = payout;
        state.winDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
          betType,
          horses,
          payout,
          limits!
        );
      } else if (betType === BET_TYPES.PLACE_TREBLE) {
        const { horses } = state.placeTreble;
        const payout = getPayouts(horses, stake);

        state.placeTreble.stake = stake;
        state.placeTreble.payout = payout;
        state.placeTreble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
          betType,
          horses,
          payout,
          limits!
        );
      } else if (betType === BET_TYPES.WIN_PLACE_DOUBLE) {
        const { horses } = state.winPlaceDouble;
        const payout = getPayouts(horses, stake);

        state.winPlaceDouble.stake = stake;
        state.winPlaceDouble.payout = payout;
        state.winPlaceDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
          betType,
          horses,
          payout,
          limits!
        );
      }

      const { updatedStake, updatedPayout } = updateTotalStakeAndPayout(
        state.winDouble,
        state.placeTreble,
        state.winPlaceDouble
      );

      state.totalStake = updatedStake;
      state.totalPayout = updatedPayout;
    },
    /*
     * Update a horse for a specific bet type
     * Params: bet type, horse & index
     */
    updateHorse: (state, action: PayloadAction<TUpdateHorse>) => {
      const { limits } = state;
      const { betType, horse, index } = action.payload;

      if (betType === BET_TYPES.WIN_DOUBLE) {
        state.winDouble.horses[index] = horse;

        const { horses, stake } = state.winDouble;
        state.winDouble.odds = getOdds(horses);
        state.winDouble.payout = getPayouts(horses, stake);
        state.winDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
          betType,
          horses,
          state.winDouble.payout,
          limits!
        );
      } else if (betType === BET_TYPES.PLACE_TREBLE) {
        state.placeTreble.horses[index] = horse;

        const { horses, stake } = state.placeTreble;
        state.placeTreble.odds = getOdds(horses);
        state.placeTreble.payout = getPayouts(horses, stake);
        state.placeTreble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
          betType,
          horses,
          state.placeTreble.payout,
          limits!
        );
      } else if (betType === BET_TYPES.WIN_PLACE_DOUBLE) {
        state.winPlaceDouble.horses[index] = horse;

        const { horses, stake } = state.winPlaceDouble;
        state.winPlaceDouble.odds = getOdds(horses);
        state.winPlaceDouble.payout = getPayouts(horses, stake);
        state.winPlaceDouble.isPayoutOverLimit = checkIfPayoutIsOverLimit(
          betType,
          horses,
          state.winPlaceDouble.payout,
          limits!
        );
      }
    },
    /*
     * Sets the currently highlighted horse from the horse selector
     * Params: horse or null;
     */
    updateHighlightedHorse: (
      state,
      action: PayloadAction<TUpdateHighlightedHorse>
    ) => {
      state.highlightedHorse = action.payload.horse;
    }
  },

  extraReducers: (builder) => {
    // GetLuckyPickData
    builder.addCase(getLuckyPickData.pending, (state) => {
      state.isFetchingLuckyPick = true;
    });
    builder.addCase(
      getLuckyPickData.fulfilled,
      (state, action: PayloadAction<TBetslipData>) => {
        state.luckyPickBettingData.bets = action.payload.bets;
        state.luckyPickBettingData.placeBets = action.payload.placeBets;
        state.luckyPickBettingData.spBets = action.payload.spBets;
        state.isFetchingLuckyPick = false;
      }
    );
    builder.addCase(getLuckyPickData.rejected, (state) => {
      state.isFetchingLuckyPick = false;
    });
  }
});

export const {
  setLuckyPickState,
  updateWinDouble,
  updatePlaceTreble,
  updateWinPlaceDouble,
  setTotalStakeAndPayout,
  distributeTotalStake,
  setUpdateStakePerBetType,
  updateHorse,
  updateHighlightedHorse
} = LuckyPickSlice.actions;

export default LuckyPickSlice.reducer;
